/**
 * Fan account creation (`/fans/join`).
 *
 * Open to anyone — no invite, no team code, no payment, no MFA. The account
 * lands in the shared `public-fanatics` realm and follows teams afterwards.
 *
 * Deliberately minimal: email, password, and an OPTIONAL first name. Every
 * extra required field costs signups, and a spectator needs none of them —
 * the name is only there so a team admin sees something friendlier than an
 * email address in their follower list.
 *
 * On success the fan goes straight to team browse (F3/F7), because an account
 * with no teams followed is an empty product. `?next=` wins when present, so a
 * fan who arrived from a team deep link lands back on that team.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useApiClient } from '../../app/providers';
import { useSession } from '../../app/session-context';
import { TextField } from '../../components/text-field';
import { useDocumentTitle } from '../../hooks/use-document-title';
import { ApiError } from '../../lib/api-error';
import { ROUTES } from '../../lib/constants';
import {
  PASSWORD_REQUIREMENT_HINTS,
  validateEmail,
  validatePassword,
} from '../auth/signup-validation';

import {
  FanAuthChrome,
  FanAuthSwitch,
  FanSocialButtons,
  StaffAuthHint,
  useFanSignInOptions,
} from './fan-auth-shared';

function toSignupMessage(error: unknown): string {
  if (ApiError.isApiError(error)) {
    if (error.status === 409) {
      return 'An account with that email already exists. Sign in instead, or reset your password.';
    }
    if (error.status === 429) return 'Too many attempts. Wait a moment and try again.';
    return error.message;
  }
  return 'Could not create your account. Check your connection and try again.';
}

export function FanSignupPage(): JSX.Element {
  useDocumentTitle('Follow your team');
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { startSession, isAuthenticated } = useSession();
  const { options } = useFanSignInOptions();

  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const nextPath = searchParams.get('next');
  // A new fan with no teams sees an empty portal, so send them to browse.
  const destination = nextPath ?? ROUTES.fanaticBrowse;

  useEffect(() => {
    if (isAuthenticated) navigate(destination, { replace: true });
  }, [isAuthenticated, navigate, destination]);

  async function onSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const errors: Record<string, string> = {};
    const emailError = validateEmail(email, 'consumer');
    const passwordError = validatePassword(password);
    if (emailError !== null) errors.email = emailError;
    if (passwordError !== null) errors.password = passwordError;
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setFormError(null);
    setIsBusy(true);
    try {
      const trimmedName = firstName.trim();
      const tokens = await apiClient.signUpFanatic({
        email: email.trim(),
        password,
        ...(trimmedName === '' ? {} : { first_name: trimmedName }),
      });
      // Signup returns a session, so a fan is never asked to sign in again
      // immediately after creating an account.
      startSession(tokens, null);
      setPassword('');
      navigate(destination, { replace: true });
    } catch (error) {
      setFormError(toSignupMessage(error));
    } finally {
      setIsBusy(false);
    }
  }

  return (
    <FanAuthChrome
      title="Follow your team"
      subtitle="Create a free account to follow teams, see live stats and watch game video."
    >
      {formError ? (
        <div className="banner banner--error" role="alert">
          <span className="banner__title">Could not create account</span>
          <span>{formError}</span>
        </div>
      ) : null}

      <form className="stack" onSubmit={(event) => void onSubmit(event)} noValidate>
        <TextField
          label="First name"
          name="first_name"
          autoComplete="given-name"
          hint="Optional — how teams will see you."
          value={firstName}
          onChange={(event) => setFirstName(event.target.value)}
        />
        <TextField
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          error={fieldErrors.email ?? null}
          onChange={(event) => setEmail(event.target.value)}
        />
        <TextField
          label="Password"
          name="password"
          type="password"
          autoComplete="new-password"
          required
          value={password}
          error={fieldErrors.password ?? null}
          hint={PASSWORD_REQUIREMENT_HINTS.join(' · ')}
          onChange={(event) => setPassword(event.target.value)}
        />
        <button type="submit" className="btn btn--primary btn--block" disabled={isBusy}>
          {isBusy ? 'Creating your account…' : 'Create account'}
        </button>
      </form>

      <FanSocialButtons
        options={options}
        redirectUri={`${window.location.origin}${ROUTES.oauthCallback}`}
        verb="Sign up"
        isBusy={isBusy}
      />

      <FanAuthSwitch mode="signup" />
      <StaffAuthHint />
    </FanAuthChrome>
  );
}
